/**
 * Cliente de la API de Locaciones (tag admin del `openapi.yaml`) — Admin Web.
 *
 * Endpoints (US-2.2):
 * - GET    /api/v1/admin/locations
 * - POST   /api/v1/admin/locations
 * - GET    /api/v1/admin/locations/{locationId}
 * - PATCH  /api/v1/admin/locations/{locationId}
 * - DELETE /api/v1/admin/locations/{locationId}
 * - GET    /api/v1/admin/locations/name-availability (TG-123: chequeo de nombre por paso)
 * - POST   /api/v1/admin/locations/{locationId}/sync (reintento de sync con el core, SUPER_ADMIN)
 *
 * Tipos derivados del contrato en `@goelectric/shared-types`; mismo patrón de red
 * que `allyApi` (sobre `apiRequest`).
 */
import type {
  AdminLocation,
  AdminLocationNameAvailability,
  AdminLocationsPage,
  CreateLocationBody,
  ListLocationsQuery,
  UpdateLocationBody,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';

/** Query params del listado (filtros + offset/limit), tal como los define el contrato. */
export type LocationListParams = ListLocationsQuery;

/** Serializa los params definidos (omite `undefined`/`null`/vacío) a `?a=b&c=d`. */
function toQuery(params: LocationListParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const locationApi = {
  list(params: LocationListParams = {}): Promise<AdminLocationsPage> {
    return apiRequest<AdminLocationsPage>(`/api/v1/admin/locations${toQuery(params)}`, {
      retryOnUnauthorized: true,
    });
  },

  get(locationId: string): Promise<AdminLocation> {
    return apiRequest<AdminLocation>(`/api/v1/admin/locations/${encodeURIComponent(locationId)}`);
  },

  create(body: CreateLocationBody): Promise<AdminLocation> {
    return apiRequest<AdminLocation>('/api/v1/admin/locations', { method: 'POST', body });
  },

  /**
   * TG-123: chequeo consultivo de unicidad del nombre al avanzar de paso. Espejo de
   * `allyApi.checkDocumentAvailability`: solo el booleano, sin datos de la locación
   * existente. La comparación es normalizada (trim + minúsculas + sin tildes) y **por
   * aliado**: el `allyId` acota el espacio de nombres; sin él consulta el de plataforma.
   */
  checkNameAvailability(name: string, allyId?: string): Promise<AdminLocationNameAvailability> {
    const query = `name=${encodeURIComponent(name)}${allyId ? `&allyId=${encodeURIComponent(allyId)}` : ''}`;
    return apiRequest<AdminLocationNameAvailability>(
      `/api/v1/admin/locations/name-availability?${query}`,
    );
  },

  update(locationId: string, body: UpdateLocationBody): Promise<AdminLocation> {
    return apiRequest<AdminLocation>(`/api/v1/admin/locations/${encodeURIComponent(locationId)}`, {
      method: 'PATCH',
      body,
    });
  },

  remove(locationId: string): Promise<void> {
    return apiRequest<void>(`/api/v1/admin/locations/${encodeURIComponent(locationId)}`, {
      method: 'DELETE',
    });
  },

  /** Reintenta la sincronización con el core CitrineOS; devuelve la locación actualizada. */
  sync(locationId: string): Promise<AdminLocation> {
    return apiRequest<AdminLocation>(
      `/api/v1/admin/locations/${encodeURIComponent(locationId)}/sync`,
      { method: 'POST' },
    );
  },
};
